import * as React from "react";
import Select from 'react-select';
import {none, Option} from "ts-option";
import {PlaceholderProps} from "../../../common/props/PlaceholderProps";
import {ActionMeta, InputActionMeta, OptionsType} from "react-select/lib/types";
import {isDefined, notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import {api} from "../../../../api/api";
import CurrencyHelper from "../../../../helper/CurrencyHelper";
import AssetSearch from "../../../../model/asset/AssetSearch";
import {RootState} from "../../../../store/reducer/root/RootState";
import {connect, Dispatch} from "react-redux";
import {saveAssetSearch} from "../../../../store/action/asset/asset";
import {AssetSearchMap} from "../../../../store/reducer/asset/AssetState";
import SingleValue, {SingleValueProps} from "react-select/lib/components/SingleValue";
import Control, {ControlProps} from "react-select/lib/components/Control";
import {getBrand} from "../../../../store/view/brand";
import {getPartnerBrand} from "../../../../store/view/partnerBrand";
import classNames = require("classnames");
import _ = require("underscore");

export interface ComponentProps {
	/**
	 * Callback on select asset.
	 * @param {Option<AssetSearch>} asset.
	 */
	onSelect: (asset: AssetSearch) => void;
	/**
	 * Initial search query text.
	 */
	initialSearchQuery?: Option<string>
	/**
	 * Excluded assets ids
	 */
	excludeIds?: string[]
	/**
	 * Selected asset id
	 */
	selectedAssetId?: string
	/**
	 * Brand identifier
	 */
	brandId?: string
	/**
	 * Partner brand identifier
	 */
	partnerBrandId?: string
	/**
	 * VAT registered
	 */
	vatRegistered?: boolean;
	/**
	 * Parts Price Strategy
	 */
	partsPriceStrategy: string
}

interface ReduxProps {
	assetSearchMap: AssetSearchMap,
	priceAdjustmentFactor: number,
	vat: number
}

interface DispatchProps {
	saveAssetSearch: (asset: AssetSearch) => void
}

interface State {
	/**
	 * Text typed by user.
	 */
	queryText: string;
	/**
	 * Assets available for user selection.
	 */
	assets: AssetSearch[];
	/**
	 * Indicates if data is loading.
	 */
	loading: boolean;
}

const minCharsToSearch = 2;

type Props = ComponentProps & PlaceholderProps & ReduxProps & DispatchProps;

class AssetSelectionComponent extends React.PureComponent<Props, State> {
	public static defaultProps: Partial<Props> = {
		initialSearchQuery: none
	};

	constructor(props: Props) {
		super(props);
		this.onInputChange = this.onInputChange.bind(this);
		this.onSelected = this.onSelected.bind(this);
		this.renderNoOptionsMessage = this.renderNoOptionsMessage.bind(this);
		this.renderOptionComponent = this.renderOptionComponent.bind(this);
		const initialQueryText = this.props.initialSearchQuery ? this.props.initialSearchQuery.getOrElse("") : "";
		this.state = {
			queryText: initialQueryText,
			assets: [],
			loading: false
		};
	}

	render() {
		const value = this.props.assetSearchMap[this.props.selectedAssetId];
		return (
			<Select
				classNamePrefix={"c-select-component"}
				inputValue={this.state.queryText}
				value={value}
				styles={{menuList: (base) => ({...base, maxHeight: "186px"})}}
				placeholder={this.props.placeholder}
				filterOption={() => true}
				components={{
					Option: this.renderOptionComponent,
					Menu: this.renderMenuComponent,
					NoOptionsMessage: this.renderNoOptionsMessage,
					SingleValue: this.renderValue,
					Control: this.renderControl
				}}
				options={this.state.assets}
				isLoading={this.state.loading}
				onInputChange={this.onInputChange}
				onChange={this.onSelected}
				closeMenuOnSelect={true}
				openMenuOnFocus={true}
				controlShouldRenderValue={isDefined(this.props.selectedAssetId)}
			/>
		)
	}

	/**
	 * Render option component (select dropdown list element)
	 * @param props properties for list element.
	 */
	renderOptionComponent(props: any): JSX.Element {
		const {innerProps, innerRef, isFocused, data, className} = props;
		const classFromProps = classNames(
			{
				"c-selection-bom-modal_select-option-component": true,
				"has-focus": isFocused
			},
			className
		);

		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className="c-selection-bom-modal_select-option-component_labels">
					<div>
							<span className="c-selection-bom-modal_select-option-component_labels_column">
								<b className="c-selection-bom-modal_select-option-component_labels_blue-color">
									{data.manufacturer.getOrElse('')}</b>
							</span>
						<span className="c-selection-bom-modal_select-option-component_labels_move-right">{data.model}</span>

					</div>
					<div className="c-selection-bom-modal_select-option-component_labels_blue-color fit-content">
						<span><b>{CurrencyHelper.toCurrency(data.getPrice(this.props.partsPriceStrategy))}</b></span>
					</div>
				</div>
			</div>
		)
	}

	/**
	 * Render menu component (wrapper for the dropdown menu in the select)
	 * @param props properties for list element.
	 */
	renderMenuComponent(props: any): JSX.Element {
		const {innerProps, innerRef, className} = props;
		const classFromProps = classNames(
			{
				"bom-selection": true
			},
			className
		);
		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className={'selection-header'}>
					<div className={'header-title'}>
						<span>Manufacturer &amp; model</span>
						<span>Sell price</span>
					</div>
					{props.children}
				</div>
			</div>
		);
	}

	renderValue = (props: SingleValueProps<AssetSearch>) => {
		const getStyles = (name: string, _props: any) => {return {...props.getStyles(name, _props), color: ""}}
		return (
			<SingleValue {...props} getStyles={getStyles} className={props.className + " t-body"}>
				{props.data.getDisplayText()}
			</SingleValue>
		)
	};

	/**
	 * Render no options component (Message to be displayed in the menu if there are no options passed in)
	 */
	renderNoOptionsMessage(): JSX.Element {
		if (this.state.loading)
			return <div>Loading...</div>;
		else if (this.state.queryText.length >= minCharsToSearch)
			return <div className="no-option">No results for '<span className="query">{this.state.queryText}</span>'</div>;
		else
			return <div className="no-option">Type to search</div>;
	}

	renderControl = (props: ControlProps<AssetSearch>): JSX.Element => {
		const getStyles = (name: string, _props: any) => {
			const value: AssetSearch | null | undefined | OptionsType<AssetSearch> = props.getValue();
			return {
				...props.getStyles(name, _props), ...((value instanceof AssetSearch && value.disabled) ||
					(value instanceof Array) && value[0] && value[0].disabled) && {backgroundColor: "#ff593c"}
			}
		};
		return (<Control {...props} getStyles={getStyles}/>)
	}

	/**
	 * On changed value in select, fetch data from store or fetch from server.
	 * @param searchText text in the input.
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(searchText: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: searchText});
			if (searchText.length >= minCharsToSearch) {
				this.findBySearchText(searchText);
			}
			else {
				this.setState({assets: [], loading: false});
			}
		}
	}

	/**
	 * Callback on selected item.
	 * @param {AssetSearch} selected item.
	 * @param {ActionMeta} actionMeta performed action data.
	 */
	onSelected(selected: AssetSearch, actionMeta: ActionMeta) {
		this.setState({queryText: "", assets: []});
		// compare with array is fix for https://github.com/JedWatson/react-select/issues/2682
		if (!(selected instanceof Array)) { //
			this.props.onSelect(selected);
			this.props.saveAssetSearch(selected);
		}
	}

	/**
	 * Reset search input text.
	 */
	public resetSearchInput() {
		this.setState({queryText: ""});
	}

	assetsSearch(searchText: string) {
		this.setState({loading: true}, () => {
			api.instance.assetApi.findAssetsByText(searchText, this.props.excludeIds)
				.then(result => this.setState({assets: result, loading: false}))
				.catch((error) => {
					this.setState({assets: [], loading: false});
					notifyBugsnagAndShowToast(error, "Error on searching assets");
				});
		});
	}

	assetsForQuoteSearch = (searchText: string) => {
		this.setState({loading: true}, () => {
			const regex = new RegExp("(\\s|^)\\d+(,\\d{3})*(\\.(\\d{1,2})?)?(\\s|$)", "g");
			const result = searchText.match(regex)
			const prices: number[] = [];
			if (result) {
				prices.push(...result.map(v => Number(v.replace(/,/g, ""))))
			}
			api.instance.assetApi.findAssetsForQuote(searchText, prices, this.props.excludeIds, this.props.brandId,
				this.props.partnerBrandId)
				.then(result => {
					this.setState({
						assets: result.map(
							a => a.increasePriceByPercentage(this.props.priceAdjustmentFactor)
								.increasePriceByPercentage(this.props.vatRegistered ? 0 : this.props.vat)),
						loading: false
					});
				})
				.catch((error) => {
					this.setState({assets: [], loading: false});
					notifyBugsnagAndShowToast(error, "Error on searching assets");
				});
		})
	}

	/**
	 * Fetch assets by search text.
	 * @param {string} searchText text to search by.
	 */
	findBySearchText = _.debounce(this.props.brandId ? this.assetsForQuoteSearch : this.assetsSearch, 300)
}

export function mapStateToProps(state: RootState, props: ComponentProps) {
	const brand = getBrand(props.brandId, state);
	return {
		assetSearchMap: state.asset.assetSearchMap,
		priceAdjustmentFactor: getPartnerBrand(props.partnerBrandId, state).map(pb => pb.priceAdjustmentFactor).getOrElse(0),
		vat: brand.map(b => b.vatRate).getOrElse(0)
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		saveAssetSearch: (asset: AssetSearch) => dispatch(saveAssetSearch(asset))
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(AssetSelectionComponent)

